import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { blogData } from "../utils/blogData";
import { formatDateDDMMMYYYY } from "../utils/dateHelpers";

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await blogData();
        setBlogs(data);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchData();
  }, []);

  // Show loading spinner while fetching data.
  if (blogs.length === 0) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="spin-loader"></div>
      </div>
    );
  }

  // Sort blogs by created_at date (newest first).
  const sortedBlogData = [...blogs].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  // Group blogs by section_name.
  const groupedBlogs = sortedBlogData.reduce((acc, blog) => {
    const section = blog.section_name || "";
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(blog);
    return acc;
  }, {});

  return (
    <div className="px-6 lg:px-8 lg:pt-14 pt-10 max-w-7xl mx-auto">
      {Object.keys(groupedBlogs).map((sectionName) => {
        const blogsInSection = groupedBlogs[sectionName];
        return (
          <div key={sectionName} className="mb-14">
            {/* Section header */}
            <h1 className="md:text-[26px] text-[24px] font-semibold mb-6 text-black">
              {sectionName}
            </h1>

            {blogsInSection.length > 0 && (
              <>
                {/* Hero Blog (first item of this section) */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:mb-16 mb-12 blog-one">
                  <img
                    src={blogsInSection[0].blog_image}
                    alt={blogsInSection[0].blog_image_alt}
                    className="w-full md:h-[340px] lg:object-cover rounded-xl shadow-lg"
                  />
                  <div>
                    <h2 className="text-[24px] font-semibold mb-2 text-black">
                      {blogsInSection[0].title}
                    </h2>
                    <p className="mb-4">
                      <span className="text-gray-900 font-medium">
                        {blogsInSection[0].author_name}
                      </span>
                    </p>
                    <p className="text-black font-normal text-[15px] mb-5">
                      {blogsInSection[0].meta_description}
                    </p>
                    <p className="text-[#4E4E4E] font-normal text-sm mb-5">
                      {formatDateDDMMMYYYY(blogsInSection[0].created_at)}
                    </p>
                    <Link
                      to={`/blogs/${blogsInSection[0].slug}`}
                      className="bg-white border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white inline-block lg:px-6 px-6 py-2 shadow-sm text-sm"
                    >
                      Read More <span className="ml-3">→</span>
                    </Link>
                  </div>
                </div>

                {/* Grid for the remaining blogs */}
                {blogsInSection.length > 1 && (
                  <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {blogsInSection.slice(1).map((blog) => (
                      <Link key={blog.blogId} to={`/blogs/${blog.slug}`}>
                        <div className="rounded-xl bg-white shadow-lg hover:shadow-xl transition transform hover:-translate-y-2">
                          <img
                            src={blog.blog_image}
                            alt={blog.blog_image_alt}
                            className="rounded-t-lg h-[200px] w-full object-cover"
                          />
                          <div className="p-6">
                            <h2 className="text-sm font-semibold mb-3">
                              {blog.title}
                            </h2>
                            <p className="text-gray-500 text-xs line-clamp-2">
                              {blog.meta_description}
                            </p>
                            <div className="flex justify-between mt-3 items-center">
                              <p className="text-gray-800 text-sm">
                                {blog.author_name}
                              </p>
                              <p className="text-gray-400 text-sm">
                                {formatDateDDMMMYYYY(blog.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Blogs;
